import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid, useMediaQuery, useTheme } from "@mui/material"
import { Select, Typography } from "@synapse-analytics/synapse-ui"
import { cloneDeep } from "lodash"
import type { LegendProps, PieProps } from "recharts"
import { Cell, Label, Legend, Pie, PieChart as ReChartsPieChart, ResponsiveContainer, Tooltip } from "recharts"

import type { LabelReasonsData } from "../../types/generated/api/LabelReasonsData"
import { customRechartsColorScheme } from "../../utils/genericHelpers"
import { KonanEmptyState } from "../utility/KonanEmptyState"
import type { ChartsContainerProps } from "./ChartsContainer"
import { ChartsContainer } from "./ChartsContainer"
import { GraphLabelText } from "./GraphLabelText"

/**
 * CustomLegend Component
 *
 * A custom legend component for the pie chart.
 *
 * @param {LegendProps} props - Legend props from Recharts
 * @param {Array<{ value: string, payload: { percent: number } }>} payload - The data for legend items
 * @return {React.ReactElement} Rendered custom legend
 */
function CustomLegend(props: LegendProps & { colors?: { background: string; text: string }[] }): React.ReactElement {
  const { t } = useTranslation("graphs")

  return (
    <Grid item pr={2} pt={0.5}>
      {props.payload?.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item: any, index: number) =>
          index < 6 && (
            <Grid container key={`legend-${item.value}`} alignItems={"center"} gap={0.5}>
              <span
                style={{
                  display: "block",
                  width: "14px",
                  height: "14px",
                  background:
                    props.colors?.[index % props.colors?.length]?.background ??
                    customRechartsColorScheme()[index % 7]?.background,
                  borderRadius: "2px",
                }}
              />
              <Typography variant="p">{item.value}</Typography>
            </Grid>
          ),
      )}

      {props.payload?.length && props.payload?.length > 6 && (
        <Grid container key={`legend-others`} alignItems={"center"} gap={0.5} ml={2.25}>
          <Typography variant="p">{t("pieChart.legend.others")}</Typography>
        </Grid>
      )}
    </Grid>
  )
}

// Define the structure of the data for the pie chart
interface PieChartData {
  name: string
  value: number
  percent: number
}

// Define the structure of the label for the pie chart
interface PieChartLabel {
  name?: string
  percent?: number
  value: number
}

// Define the props for the PieChart component
interface PieChartProps extends Omit<PieProps, "dataKey"> {
  dataKey?: string
  graphHeight?: number | string
  data: PieChartData[]
  label?: PieChartLabel
  orientation?: "vertical" | "horizontal"
  colors?: { background: string; text: string }[]
}

/**
 * PieChart Component
 *
 * A reusable pie chart component using Recharts without the ChartsContainer wrapper.
 *
 * @param {string} [dataKey="value"] - The key to use for data values
 * @param {PieChartData[]} data - The data for the pie chart
 * @param {PieChartLabel} [label] - Optional label for the chart
 * @param {"vertical" | "horizontal"} [orientation="horizontal"] - The orientation of the chart
 * @param {number} [graphHeight] - The height of the graph
 * @returns {React.ReactElement} The rendered PieChart component
 */
export function PieChart(props: Readonly<PieChartProps>): React.ReactElement {
  const { dataKey = "value", data, label, colors, ...pieProps } = props
  const { t } = useTranslation("graphs")

  const [isMounted, setIsMounted] = useState<boolean>(false)

  /**
   * Effect hook to handle delayed mounting of chart labels
   *
   * This is used to prevent a race condition where the Label component might try to render
   * before the chart's dimensions are properly calculated. The small delay ensures the
   * chart is fully rendered before showing labels.
   *
   * @effect Sets isMounted to true after a 100ms delay
   * @dependencies Empty array - only runs once on component mount
   */
  useEffect(() => {
    setTimeout(() => {
      setIsMounted(true)
    }, 100)
  }, [])

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ReChartsPieChart width={400} height={160}>
        <Pie
          data={data}
          dataKey={dataKey}
          cx={130}
          cy="100%"
          endAngle={0}
          startAngle={180}
          innerRadius={90}
          outerRadius={115}
          stroke="none"
          paddingAngle={pieProps.paddingAngle}
        >
          {/* Render cells for each data point */}
          {data?.map((_entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors?.[index % colors?.length]?.background ?? customRechartsColorScheme()[index % 7]?.background}
            />
          ))}

          {/* Render custom label */}
          {isMounted && !!label && (
            <Label
              cy={"70%"}
              cx={100}
              position="inside"
              content={({ cy, cx }) => {
                const xOffset = label.value.toString().length > 0 ? label.value.toString().length * 5.6 : 0
                const yOffset = label.name ? 15 : 0

                return (
                  <foreignObject
                    // Magic numbers to center the number inside the graph
                    x={`calc(${cx}px + 25px - ${xOffset}px)`}
                    y={`calc(${cy} - ${yOffset}px)`}
                    style={{ overflow: "visible" }}
                  >
                    <GraphLabelText label={label} />
                  </foreignObject>
                )
              }}
            />
          )}
        </Pie>

        {/* Render custom legend */}
        <Legend
          layout="vertical"
          verticalAlign="top"
          align="right"
          content={(props) => (
            <CustomLegend
              {...(props as LegendProps)}
              colors={colors as { background: string; text: string }[] | undefined}
            />
          )}
        />

        <Tooltip
          content={({ payload }) =>
            payload?.length && (
              <div className="chart-tooltip" style={{ width: "fit-content" }}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                  <Grid item>
                    <span
                      key="chip"
                      style={{
                        display: "block",
                        width: "14px",
                        height: "14px",
                        background: payload?.[0]?.payload.fill,
                        borderRadius: "2px",
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="h3-bold" display="block" variantColor={2}>
                      {payload?.[0]?.payload.name}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="p" variantColor={2}>
                  {t("pieChart.tooltip.count")} {payload?.[0]?.payload.value}
                </Typography>
                <Typography variant="p" variantColor={2}>
                  {t("pieChart.tooltip.percentage")} {payload?.[0]?.payload.payload?.percent?.toFixed(1) + "%"}
                </Typography>
              </div>
            )
          }
        />
      </ReChartsPieChart>
    </ResponsiveContainer>
  )
}
// Define the props for the BasePieChart component
interface BasePieChartProps extends Omit<ChartsContainerProps, "children" | "emptyState">, PieChartProps {
  emptyState?: { title: string; subTitle?: string }
  reportType?: "simulation"
}

/**
 * BasePieChart Component
 *
 * A pie chart component that includes the ChartsContainer wrapper.
 *
 * @param {BasePieChartProps} props - The props for the BasePieChart component
 * @param {string} title - The title of the chart
 * @param {string} [tooltipText] - Optional tooltip text
 * @param {number} [range] - Optional range value
 * @param {boolean} [isLoading=false] - Whether the chart is in a loading state
 * @param {{ options: string[], value: string, setValue: (val: string) => void }} [selectProps] - Optional props for a select component
 * @param {"full" | "select" | "none"} [headerMode] - The mode for the header display
 * @param {number} [graphHeight=400] - The height of the graph
 * @returns {React.ReactElement} The rendered BasePieChart component
 */
export function BasePieChart(props: Readonly<BasePieChartProps>): React.ReactElement {
  const { t } = useTranslation("graphs")

  const {
    title,
    tooltipText,
    range,
    isLoading = false,
    selectProps,
    headerMode,
    graphHeight = 400,
    emptyState,
    customHeaderComponent,
    reportType,
    ...pieChartProps
  } = props

  return (
    <ChartsContainer
      title={title}
      reportType={reportType}
      tooltipText={tooltipText}
      range={range}
      isLoading={isLoading}
      graphHeight={graphHeight}
      selectProps={selectProps}
      headerMode={headerMode}
      customHeaderComponent={customHeaderComponent}
    >
      {emptyState ? (
        <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
          <KonanEmptyState
            title={emptyState?.title ?? t("pieChart.emptyState.unavailable")}
            subTitle={emptyState?.subTitle}
          />
        </Grid>
      ) : (
        <PieChart {...pieChartProps} />
      )}
    </ChartsContainer>
  )
}

/**
 * Props for the PieContainer component
 */
interface PieContainerProps {
  label: string
  labelReasons: {
    labels: string[]
    labelReasons: Record<string, LabelReasonsData> | undefined
  }
  // TODO: Define a more specific type for pieChartProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pieChartProps: any
  graphHeight: number | string
}

/**
 * PieContainer Component
 *
 * Renders a select dropdown and a pie chart for a specific label.
 *
 * @param {PieContainerProps} props - The props for the PieContainer component
 * @param {string} label - The current label
 * @param {{ labels: string[], labelReasons: Record<string, { reasons: any[] }> }} labelReasons - The label reasons data
 * @param {Omit<PieChartProps, 'data' | 'graphHeight'>} pieChartProps - Props for the PieChart component
 * @param {number} graphHeight - The height of the graph
 * @returns {React.ReactElement} The rendered PieContainer component
 */
export const PieContainer = (props: PieContainerProps): React.ReactElement => {
  const { t } = useTranslation("graphs")
  const { label, labelReasons, pieChartProps, graphHeight } = props
  const [selectedLabel, setSelectedLabel] = useState<string | undefined>(label)

  return (
    <Fragment>
      {/* Dropdown for selecting a label */}
      <Grid item columnSpacing={1} xs={12} flexWrap={"nowrap"}>
        <Select
          options={labelReasons.labels}
          value={selectedLabel}
          handleChange={(e) => setSelectedLabel(e.target.value as string)}
          placeholder={t("pieChart.selectPlaceholder")}
          fullWidth
          menuProps={{ menuMaxContent: true }}
        />
      </Grid>

      {/* Pie chart for the selected label */}
      {selectedLabel && (
        <Grid item xs={12}>
          <PieChart
            {...pieChartProps}
            data={labelReasons.labelReasons?.[selectedLabel]?.reasons}
            graphHeight={graphHeight}
            orientation="vertical"
            label={{
              name: t("pieChart.label.total"),
              value: labelReasons.labelReasons?.[selectedLabel]?.count,
            }}
          />
        </Grid>
      )}
    </Fragment>
  )
}

/**
 * Props for the ReasonsBreakdownChart component
 */
interface ReasonsBreakdownChartProps extends Omit<BasePieChartProps, "data" | "label"> {
  data?: Record<string, LabelReasonsData>
  emptyState?: { title: string }
}

/**
 * ReasonsBreakdownChart Component
 *
 * Renders a chart container with multiple pie charts for different labels.
 *
 * @param {string} title - The title of the chart
 * @param {string} [tooltipText] - Optional tooltip text
 * @param {boolean} [isLoading=false] - Whether the chart is in a loading state
 * @param {number} [graphHeight=400] - The height of the graph
 * @param {Record<string, LabelReasonsData>} data - The data for the chart
 * @returns {React.ReactElement} The rendered ReasonsBreakdownChart component
 */
export function ReasonsBreakdownChart(props: Readonly<ReasonsBreakdownChartProps>): React.ReactElement {
  const { t } = useTranslation("graphs")
  const { title, tooltipText, isLoading = false, graphHeight = 400, data: labels, emptyState, ...pieChartProps } = props

  const theme = useTheme()
  const md = useMediaQuery(theme.breakpoints.up("md"))
  const lg = useMediaQuery(theme.breakpoints.up("lg"))
  const xl = useMediaQuery(theme.breakpoints.up("xl"))

  // Process the label data and calculate percentages
  const labelReasons = useMemo(() => {
    if (!labels) return { labels: [], labelReasons: {} }
    const labelsCopy = cloneDeep(labels)

    if (isLoading || emptyState?.title) return { labels: [], labelReasons: {} }

    // Calculate percentages for each reason
    for (const key in labelsCopy) {
      const formattedReasons = labelsCopy[key]?.reasons.map((reason) => ({
        name: reason.reason,
        value: reason.count,
        percent: (reason.count / (labelsCopy[key]?.count ?? 0)) * 100,
        reason: reason.reason,
        count: reason.count,
      }))

      if (labelsCopy[key]?.reasons && formattedReasons) {
        labelsCopy[key].reasons = formattedReasons
      }
    }

    return { labels: Object.keys(labelsCopy), labelReasons: labelsCopy }
  }, [emptyState?.title, isLoading, labels])

  const sliceNumberCalculator = (): number => {
    const labelReasonsCount = Object.keys(labelReasons.labelReasons).length

    if (xl) {
      return Math.min(4, labelReasonsCount)
    } else if (lg) {
      return Math.min(3, labelReasonsCount)
    } else if (md) {
      return Math.min(2, labelReasonsCount)
    }

    return 1
  }

  return (
    <ChartsContainer
      title={title}
      tooltipText={tooltipText}
      isLoading={isLoading}
      graphHeight={graphHeight}
      headerMode={"full"}
      gutterBottom
    >
      <Grid container columnSpacing={1} justifyContent={"space-around"}>
        {!labelReasons.labelReasons || Object.keys(labelReasons.labelReasons).length === 0 || emptyState ? (
          <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
            <KonanEmptyState title={emptyState?.title ?? t("reasonsBreakdownChart.emptyState.unavailable")} />
          </Grid>
        ) : (
          Object.entries(labelReasons.labelReasons)
            .slice(0, sliceNumberCalculator())
            .map(
              ([key, _], index): React.ReactElement => (
                <Grid container item xs={12 / sliceNumberCalculator()} key={`container-${index}`} height={"100%"}>
                  <PieContainer
                    key={key}
                    label={key}
                    labelReasons={labelReasons}
                    pieChartProps={pieChartProps}
                    graphHeight={graphHeight}
                  />
                </Grid>
              ),
            )
        )}
      </Grid>
    </ChartsContainer>
  )
}
